import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { X, Save, ShieldCheck, Ban } from "lucide-react";

export default function Profile() {
  const { user, refresh } = useAuth();
  const [display_name, setName] = useState(user?.display_name || "");
  const [bio, setBio] = useState(user?.bio || "");
  const [tag, setTag] = useState("");
  const [interests, setInterests] = useState(user?.interests || []);
  const [availability, setAvailability] = useState(user?.availability || "available");
  const [busy, setBusy] = useState(false);

  // Signature state
  const [sigSet, setSigSet] = useState(false);
  const [sigCurrentPw, setSigCurrentPw] = useState("");
  const [sigValue, setSigValue] = useState("");
  const [sigBusy, setSigBusy] = useState(false);

  // Blocklist
  const [blocked, setBlocked] = useState([]);

  useEffect(() => {
    api.get("/auth/signature/status").then((r) => setSigSet(!!r.data.set)).catch(() => {});
    api.get("/users/blocklist").then((r) => setBlocked(r.data.blocked || [])).catch(() => {});
  }, []);

  const unblock = async (id) => {
    await api.post(`/users/unblock/${id}`);
    setBlocked((prev) => prev.filter((u) => u.id !== id));
    toast.success("Unblocked");
  };

  const saveSignature = async (e) => {
    e.preventDefault();
    if (sigValue.length < 6) { toast.error("Signature must be 6+ characters"); return; }
    setSigBusy(true);
    try {
      await api.post("/auth/signature", { current_password: sigCurrentPw, signature: sigValue });
      toast.success(sigSet ? "Signature rotated" : "Signature set");
      setSigSet(true);
      setSigCurrentPw(""); setSigValue("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not set signature");
    } finally { setSigBusy(false); }
  };

  const addTag = () => {
    const t = tag.trim();
    if (!t) return;
    if (interests.includes(t)) return;
    setInterests([...interests, t]);
    setTag("");
  };
  const removeTag = (t) => setInterests(interests.filter((x) => x !== t));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.patch("/profile", { display_name, bio, interests, availability });
      await refresh();
      toast.success("Profile saved");
    } catch { toast.error("Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="px-4 md:px-8 py-6 max-w-2xl mx-auto">
      <div className="text-xs tracking-[0.3em] font-mono text-[#00E5FF]">// SIGNAL PROFILE</div>
      <h1 className="font-[Unbounded] text-3xl md:text-4xl font-bold tracking-tighter mt-2 mb-8">Your broadcast</h1>

      <form onSubmit={save} className="space-y-5">
        <div>
          <label className="text-[10px] tracking-widest font-mono text-white/50 uppercase">Display Name</label>
          <input data-testid="profile-name-input" value={display_name} onChange={(e) => setName(e.target.value)}
            className="mt-2 w-full bg-[#0D0E15] border border-white/10 rounded-lg px-4 py-3 focus:border-[#00E5FF] focus:outline-none" />
        </div>

        <div>
          <label className="text-[10px] tracking-widest font-mono text-white/50 uppercase">Bio</label>
          <textarea data-testid="profile-bio-input" value={bio} onChange={(e) => setBio(e.target.value)} rows={3}
            placeholder="A line the AI can use to match you."
            className="mt-2 w-full bg-[#0D0E15] border border-white/10 rounded-lg px-4 py-3 focus:border-[#00E5FF] focus:outline-none" />
        </div>

        <div>
          <label className="text-[10px] tracking-widest font-mono text-white/50 uppercase">Availability</label>
          <div className="mt-2 flex gap-2">
            {["available", "busy", "away"].map((v) => (
              <button
                key={v}
                type="button"
                data-testid={`availability-${v}`}
                onClick={() => setAvailability(v)}
                className={`px-4 py-2 rounded-full text-xs font-mono uppercase tracking-widest border transition-all ${
                  availability === v
                    ? "bg-[#00E5FF] text-black border-[#00E5FF]"
                    : "border-white/10 text-white/60 hover:border-white/30"
                }`}
              >
                {v}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="text-[10px] tracking-widest font-mono text-white/50 uppercase">Interests · Preloaded info the AI reads</label>
          <div className="mt-2 flex gap-2">
            <input
              data-testid="profile-tag-input"
              value={tag}
              onChange={(e) => setTag(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
              placeholder="e.g. climbing, jazz, startups"
              className="flex-1 bg-[#0D0E15] border border-white/10 rounded-lg px-4 py-3 focus:border-[#00E5FF] focus:outline-none"
            />
            <button data-testid="add-tag-btn" type="button" onClick={addTag}
              className="px-4 bg-white/5 border border-white/10 rounded-lg hover:bg-white/10 transition-all">Add</button>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {interests.map((t) => (
              <span key={t} data-testid={`tag-chip-${t}`} className="inline-flex items-center gap-1 text-xs font-mono px-3 py-1 rounded-full bg-[#00E5FF]/10 border border-[#00E5FF]/30 text-[#00E5FF]">
                {t}
                <button type="button" onClick={() => removeTag(t)}><X size={12} /></button>
              </span>
            ))}
          </div>
        </div>

        <div className="p-5 bg-[#0D0E15] border border-white/10 rounded-2xl">
          <div className="flex items-center gap-2">
            <ShieldCheck size={14} className="text-[#00E5FF]" />
            <div className="text-[10px] tracking-widest font-mono text-white/60">APPROVAL SIGNATURE</div>
            {sigSet && <span data-testid="sig-status" className="ml-auto text-[10px] font-mono uppercase text-[#39FF14]">Active</span>}
          </div>
          <p className="text-xs text-white/50 mt-2">
            A private passphrase used to approve any AI-suggested action. Stored bcrypt-hashed — it never leaves the server in plain form.
          </p>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
            <input
              data-testid="sig-current-password"
              type="password"
              value={sigCurrentPw}
              onChange={(e) => setSigCurrentPw(e.target.value)}
              placeholder="Your login password"
              className="bg-[#05050A] border border-white/10 rounded-lg px-4 py-3 focus:border-[#00E5FF] focus:outline-none"
            />
            <input
              data-testid="sig-new-signature"
              type="password"
              value={sigValue}
              onChange={(e) => setSigValue(e.target.value)}
              placeholder={sigSet ? "New signature (6+ chars)" : "Signature (6+ chars)"}
              className="bg-[#05050A] border border-white/10 rounded-lg px-4 py-3 focus:border-[#00E5FF] focus:outline-none"
            />
          </div>
          <button
            data-testid="save-signature-btn"
            type="button"
            onClick={saveSignature}
            disabled={sigBusy || !sigCurrentPw || !sigValue}
            className="mt-3 inline-flex items-center gap-2 bg-white text-black font-semibold px-4 py-2 rounded-full disabled:opacity-50"
          >
            <ShieldCheck size={14} /> {sigSet ? "Rotate signature" : "Set signature"}
          </button>
        </div>

        <div className="p-5 bg-[#0D0E15] border border-white/10 rounded-2xl">
          <div className="text-[10px] tracking-widest font-mono text-white/40">CURRENT SUBSCRIPTION</div>
          <div className="mt-2 flex items-center justify-between gap-4 flex-wrap">
            <div className="font-[Unbounded] text-2xl">
              {user?.subscription_tier === "pro" ? (
                <span className="text-[#39FF14]">PRO</span>
              ) : "FREE"}
            </div>
            {user?.subscription_tier !== "pro" && (
              <button
                data-testid="upgrade-pro-btn"
                type="button"
                disabled={busy}
                onClick={async () => {
                  try {
                    setBusy(true);
                    const { data } = await api.post("/payments/checkout", {
                      lookup_key: "pro_monthly",
                      origin_url: window.location.origin,
                    });
                    window.location.href = data.checkout_url;
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || "Could not start checkout");
                    setBusy(false);
                  }
                }}
                className="inline-flex items-center gap-2 bg-[#39FF14] text-black font-semibold px-5 py-2.5 rounded-full hover:glow-secondary transition-all disabled:opacity-50"
              >
                Upgrade to Pro · $9/mo
              </button>
            )}
          </div>
          <p className="text-xs text-white/50 mt-3">Pro subscribers get deeper AI reasoning, wider scan range, and priority signal ranking. Cancel any time in Stripe.</p>
        </div>

        <div className="p-5 bg-[#0D0E15] border border-white/10 rounded-2xl">
          <div className="flex items-center gap-2">
            <Ban size={14} className="text-[#FF007F]" />
            <div className="text-[10px] tracking-widest font-mono text-white/60">BLOCKED USERS</div>
            <span data-testid="blocked-count" className="ml-auto text-[10px] font-mono uppercase text-white/40">{blocked.length}</span>
          </div>
          <p className="text-xs text-white/50 mt-2">
            Anyone whose signal you declined is auto-blocked — they can&apos;t ping you and won&apos;t see you on the radar. Unblock any time.
          </p>
          {blocked.length === 0 ? (
            <div data-testid="empty-blocklist" className="mt-3 text-xs font-mono text-white/40">No one blocked.</div>
          ) : (
            <div className="mt-3 flex flex-wrap gap-2">
              {blocked.map((u) => (
                <span key={u.id} data-testid={`blocked-${u.id}`} className="inline-flex items-center gap-2 text-xs font-mono px-3 py-1 rounded-full bg-[#FF007F]/10 border border-[#FF007F]/30 text-[#FF007F]">
                  {u.display_name || u.id.slice(0, 6)}
                  <button data-testid={`unblock-${u.id}`} type="button" onClick={() => unblock(u.id)}
                    className="text-white/50 hover:text-white transition-all"><X size={12} /></button>
                </span>
              ))}
            </div>
          )}
        </div>

        <button data-testid="save-profile-btn" disabled={busy}
          className="w-full bg-[#00E5FF] text-black font-semibold py-3 rounded-full hover:glow-primary transition-all disabled:opacity-50 inline-flex items-center justify-center gap-2">
          <Save size={16} /> {busy ? "Saving…" : "Save Profile"}
        </button>
      </form>
    </div>
  );
}
